/**
 * Central navigation config for VAULT.
 *
 * SIDEBAR_GROUPS  — 12 top-level sections shown in the desktop sidebar,
 *                   each with a submenu (children) matching the routes
 *                   that already exist. Roles gate visibility per item.
 *                   Sections with zero visible children auto-hide.
 * SUPER_ADMIN_GROUP — separate, admin-only, isolated from the customer
 *                   workspace menu (renders in its own footer block).
 * BOTTOM_TABS     — mobile bottom navigation (Home / Action / Add /
 *                   Reports / More). The "Add" tab opens a bottom-sheet
 *                   of quick-add actions; "More" opens the full drawer.
 * BOTTOM_ADD_ACTIONS — items shown inside the mobile "Add" bottom sheet.
 *
 * Only routes that ALREADY EXIST are referenced (the user said
 * "Do not add new modules"). Where a submenu label doesn't map 1:1 to a
 * dedicated page, the closest existing route is used with an optional
 * `hash` or `query` deep-link (e.g. Reports → /reports?tab=card).
 */
import {
    LayoutDashboard, ClipboardCheck, Wallet, Receipt, Banknote, FileText,
    Repeat, ScrollText, Package, FileBarChart, ShieldCheck,
    Settings as SettingsIcon,
    CreditCard, Landmark, Coins, Globe, Plane,
    ArrowUpFromLine, Zap, Camera, Tag,
    KeyRound, RefreshCcw, FileWarning,
    Calendar, Search, XCircle,
    FolderLock, ShieldAlert, Award,
    LineChart, TrendingUp, Home as HomeIcon,
    History, Users as UsersIcon, Bell, DatabaseZap,
    Building2, BadgeDollarSign, Mail, Handshake, Activity,
    Sparkles, PieChart, Plus, Menu as MenuIcon, Upload, Heart, Rocket, Server,
} from "lucide-react";

export const ROLE_ALL = ["admin", "finance", "expense_user", "viewer"];
export const ROLE_ADMIN_FIN = ["admin", "finance"];
export const ROLE_ADMIN_FIN_VIEW = ["admin", "finance", "viewer"];
export const ROLE_ADMIN_FIN_ENTRY = ["admin", "finance", "expense_user"];
export const ROLE_ADMIN_ONLY = ["admin"];

/** 12-section customer workspace sidebar. */
export const SIDEBAR_GROUPS = [
    {
        id: "dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        to: "/dashboard",
        roles: ROLE_ALL,
    },
    {
        id: "action",
        label: "Action Center",
        icon: ClipboardCheck,
        to: "/finance-review",
        roles: ROLE_ADMIN_FIN_VIEW,
    },
    {
        id: "together",
        label: "VAULT Together",
        icon: HomeIcon,
        roles: ROLE_ALL,
        children: [
            { to: "/household",               label: "Overview",       icon: HomeIcon,      roles: ROLE_ALL },
            { to: "/household/goals",         label: "Shared Goals",   icon: TrendingUp,    roles: ROLE_ALL },
            { to: "/household/tasks",         label: "Tasks",          icon: ClipboardCheck,roles: ROLE_ALL },
            { to: "/household/money-map",     label: "Money Map",      icon: PieChart,      roles: ROLE_ALL },
            { to: "/household/reviews",       label: "Reviews",        icon: Search,        roles: ROLE_ALL },
            { to: "/household/checkin",       label: "Couple Check-In",icon: Heart,         roles: ROLE_ALL },
            { to: "/household/family-review", label: "Family Review",  icon: UsersIcon,     roles: ROLE_ALL },
            { to: "/household/summary",       label: "Summary",        icon: FileBarChart,  roles: ROLE_ALL },
            { to: "/household/onboarding",    label: "Onboarding",     icon: Sparkles,      roles: ROLE_ALL },
        ],
    },
    {
        id: "money",
        label: "Money",
        icon: Wallet,
        roles: ROLE_ADMIN_FIN_VIEW,
        children: [
            { to: "/cards", label: "Credit Cards", icon: CreditCard, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/bank-accounts", label: "Bank Accounts", icon: Landmark, roles: ROLE_ADMIN_FIN },
            { to: "/cash-desk", label: "Cash Accounts", icon: Coins, roles: ROLE_ALL },
            { to: "/forex", label: "Forex Rates", icon: Globe, roles: ROLE_ALL },
            { to: "/forex-cards", label: "Forex Cards", icon: Plane, roles: ROLE_ADMIN_FIN_VIEW },
        ],
    },
    {
        id: "expenses",
        label: "Expenses",
        icon: Receipt,
        roles: ROLE_ALL,
        children: [
            { to: "/quick-entry", label: "Add Expense", icon: Zap, roles: ROLE_ADMIN_FIN_ENTRY },
            { to: "/review-queue", label: "Ready to Review", icon: ClipboardCheck, roles: ROLE_ADMIN_FIN_ENTRY },
            { to: "/organise/vault-inbox", label: "VAULT Inbox", icon: Mail, roles: ROLE_ADMIN_FIN_ENTRY },
            { to: "/expenses", label: "Card Expenses", icon: CreditCard, roles: ROLE_ALL },
            { to: "/cash-desk?tab=ledger", label: "Cash Expenses", icon: Coins, roles: ROLE_ALL },
            { to: "/smart-inbox", label: "Receipt Tracker", icon: Camera, roles: ROLE_ADMIN_FIN },
            { to: "/settings?tab=categories", label: "Categories", icon: Tag, roles: ROLE_ADMIN_FIN },
        ],
    },
    {
        id: "payments",
        label: "Payments",
        icon: Banknote,
        roles: ROLE_ADMIN_FIN_VIEW,
        children: [
            { to: "/payments?add=1", label: "Record Payment", icon: Plus, roles: ROLE_ADMIN_FIN },
            { to: "/payments?type=card", label: "Card Payments", icon: CreditCard, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/payments?type=bank", label: "Bank Payments", icon: Landmark, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/documents?type=payment_proof", label: "Payment Proofs", icon: FolderLock, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/reconciliation", label: "Reflection Status", icon: RefreshCcw, roles: ROLE_ADMIN_FIN },
        ],
    },
    {
        id: "statements",
        label: "Statements",
        icon: FileText,
        roles: ROLE_ADMIN_FIN_VIEW,
        children: [
            { to: "/statements", label: "Statement Repository", icon: FileText, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/email-capture", label: "Upload Statement", icon: Upload, roles: ROLE_ADMIN_FIN },
            { to: "/statement-passwords", label: "Statement Passwords", icon: KeyRound, roles: ROLE_ADMIN_FIN },
            { to: "/reconciliation", label: "Reconciliation", icon: RefreshCcw, roles: ROLE_ADMIN_FIN },
            { to: "/statements?filter=missing", label: "Missing Statements", icon: FileWarning, roles: ROLE_ADMIN_FIN_VIEW },
        ],
    },
    {
        id: "subs",
        label: "Subscriptions",
        icon: Repeat,
        roles: ROLE_ADMIN_FIN_VIEW,
        children: [
            { to: "/subscriptions", label: "Active Subscriptions", icon: Repeat, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/calendar", label: "Renewal Calendar", icon: Calendar, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/subscriptions?tab=review", label: "Subscription Review", icon: Search, roles: ROLE_ADMIN_FIN },
            { to: "/subscriptions?tab=cancelled", label: "Cancellation Tracker", icon: XCircle, roles: ROLE_ADMIN_FIN_VIEW },
        ],
    },
    {
        id: "docs",
        label: "Documents",
        icon: ScrollText,
        roles: ROLE_ADMIN_FIN_VIEW,
        children: [
            { to: "/documents", label: "Document Vault", icon: FolderLock, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/documents?type=receipt", label: "Receipts", icon: Receipt, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/documents?type=warranty", label: "Warranties", icon: ShieldCheck, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/documents?type=insurance", label: "Insurance Documents", icon: ShieldCheck, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/documents?type=asset", label: "Asset Documents", icon: Package, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/documents?type=payment_proof", label: "Payment Proofs", icon: Banknote, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/organise/travel", label: "Travel · Boarding Passes", icon: Plane, roles: ROLE_ALL },
            { to: "/together/people-access", label: "People & access", icon: UsersIcon, roles: ROLE_ADMIN_FIN_VIEW },
        ],
    },
    {
        id: "assets",
        label: "Assets & Net Worth",
        icon: Package,
        roles: ROLE_ADMIN_FIN_VIEW,
        children: [
            { to: "/assets", label: "Assets", icon: Package, roles: ROLE_ALL },
            { to: "/investments", label: "Investments", icon: LineChart, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/investments?tab=liabilities", label: "Liabilities", icon: TrendingUp, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/documents?type=warranty", label: "Warranties", icon: ShieldCheck, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/finance-review#networth", label: "Net Worth Summary", icon: PieChart, roles: ROLE_ADMIN_FIN_VIEW },
        ],
    },
    {
        id: "reports",
        label: "Reports",
        icon: FileBarChart,
        roles: ROLE_ADMIN_FIN_VIEW,
        children: [
            { to: "/reports?tab=monthly", label: "Monthly Summary", icon: Calendar, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/reports/cash-flow", label: "Cash Flow (Sankey)", icon: PieChart, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/reports?tab=cards", label: "Card Reports", icon: CreditCard, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/reports?tab=cash", label: "Cash Reports", icon: Coins, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/reports?tab=expenses", label: "Expense Reports", icon: Receipt, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/tax-centre", label: "GST Reports", icon: BadgeDollarSign, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/reports?tab=networth", label: "Net Worth Reports", icon: PieChart, roles: ROLE_ADMIN_FIN_VIEW },
            { to: "/auditor-shares", label: "CA / Auditor Pack", icon: Handshake, roles: ROLE_ADMIN_FIN },
        ],
    },
    {
        id: "security",
        label: "Security Center",
        icon: ShieldCheck,
        roles: ROLE_ADMIN_FIN_VIEW,
        children: [
            { to: "/security", label: "Data Protection", icon: ShieldCheck, roles: ROLE_ALL },
            { to: "/audit-trail", label: "Audit Logs", icon: ScrollText, roles: ROLE_ADMIN_FIN },
            { to: "/audit-trail?event=login", label: "User Access Logs", icon: UsersIcon, roles: ROLE_ADMIN_FIN },
            { to: "/audit-trail?event=document_download", label: "Document Download Logs", icon: FolderLock, roles: ROLE_ADMIN_FIN },
            { to: "/audit-trail?event=export", label: "Export Logs", icon: FileBarChart, roles: ROLE_ADMIN_FIN },
            { to: "/audit-trail?event=stmt_pw_reveal", label: "Statement Password Reveals", icon: KeyRound, roles: ROLE_ADMIN_FIN },
        ],
    },
    {
        id: "settings",
        label: "Settings",
        icon: SettingsIcon,
        roles: ROLE_ADMIN_ONLY,
        children: [
            { to: "/users", label: "Users", icon: UsersIcon, roles: ROLE_ADMIN_ONLY },
            { to: "/users?tab=roles", label: "Roles", icon: ShieldCheck, roles: ROLE_ADMIN_ONLY },
            { to: "/settings", label: "Workspace Settings", icon: Building2, roles: ROLE_ADMIN_ONLY },
            { to: "/settings/connected-accounts", label: "Connected Accounts", icon: Landmark, roles: ROLE_ADMIN_ONLY },
            { to: "/settings?tab=categories", label: "Categories", icon: Tag, roles: ROLE_ADMIN_ONLY },
            { to: "/alerts?tab=rules", label: "Notification Rules", icon: Bell, roles: ROLE_ADMIN_ONLY },
            { to: "/pricing", label: "SaaS Plan Settings", icon: BadgeDollarSign, roles: ROLE_ADMIN_ONLY },
            { to: "/settings?tab=tenant", label: "Workspace", icon: Building2, roles: ROLE_ADMIN_ONLY },
        ],
    },
];

/** Minimal sidebar shown by default on desktop after the Concierge
 * redesign. Grouped labels aligned with the SaaS nav spec:
 * Home · Money · Wealth · Obligations · Documents · Insights · Reports.
 * Everything else lives under `/tools`. The full `SIDEBAR_GROUPS`
 * above is still used by AllTools + as the source of truth for the
 * mobile More drawer. */
export const MINIMAL_SIDEBAR = [
    { id: "home",         label: "Home",         icon: LayoutDashboard, to: "/dashboard",     roles: ROLE_ALL },
    { id: "money",        label: "Money",        icon: Wallet,          to: "/cards",         roles: ROLE_ADMIN_FIN_VIEW },
    { id: "wealth",       label: "Wealth",       icon: LineChart,       to: "/investments",   roles: ROLE_ADMIN_FIN_VIEW },
    { id: "obligations",  label: "Obligations",  icon: Repeat,          to: "/subscriptions", roles: ROLE_ADMIN_FIN_VIEW },
    { id: "documents",    label: "Documents",    icon: FolderLock,      to: "/documents",     roles: ROLE_ADMIN_FIN_VIEW },
    { id: "insights",     label: "Insights",     icon: Sparkles,        to: "/finance-review",roles: ROLE_ADMIN_FIN_VIEW },
    { id: "ai",           label: "Ask VAULT",    icon: Sparkles,        to: "/ai",            roles: ROLE_ALL },
    { id: "reports-min",  label: "Reports",      icon: FileBarChart,    to: "/reports",       roles: ROLE_ADMIN_FIN_VIEW },
    { id: "security-min", label: "Security",     icon: ShieldCheck,     to: "/security",      roles: ROLE_ALL },
    { id: "settings-min", label: "Settings",     icon: SettingsIcon,    to: "/settings",      roles: ROLE_ADMIN_ONLY },
    { id: "all-tools",    label: "All Tools",    icon: MenuIcon,        to: "/tools",         roles: ROLE_ALL },
];

/** Super Admin — platform-owner only, isolated from customer menu. */
export const SUPER_ADMIN_GROUP = {
    id: "super",
    label: "Super Admin",
    icon: Building2,
    roles: ROLE_ADMIN_ONLY,
    children: [
        { to: "/super-admin/command-centre", label: "Command Centre", icon: Activity, roles: ROLE_ADMIN_ONLY },
        { to: "/super-admin/customer-success", label: "Customer Success", icon: Heart, roles: ROLE_ADMIN_ONLY },
        { to: "/super-admin/launch-centre", label: "Launch Centre", icon: Rocket, roles: ROLE_ADMIN_ONLY },
        { to: "/admin/feature-health", label: "Feature Health", icon: Activity, roles: ROLE_ADMIN_ONLY },
        { to: "/admin/authentication", label: "Authentication Metrics", icon: KeyRound, roles: ROLE_ADMIN_ONLY },
        { to: "/admin/integrations/providers", label: "Integrations · Providers", icon: DatabaseZap, roles: ROLE_ADMIN_ONLY },
        { to: "/super-admin/releases", label: "Releases", icon: Server, roles: ROLE_ADMIN_ONLY },
        { to: "/super-admin", label: "Platform Dashboard", icon: LayoutDashboard, roles: ROLE_ADMIN_ONLY },
        { to: "/super-admin?tab=tenants", label: "Workspaces", icon: Building2, roles: ROLE_ADMIN_ONLY },
        { to: "/super-admin?tab=users", label: "Users", icon: UsersIcon, roles: ROLE_ADMIN_ONLY },
        { to: "/pricing", label: "Plans", icon: BadgeDollarSign, roles: ROLE_ADMIN_ONLY },
        { to: "/admin/paddle-readiness", label: "Billing Status", icon: BadgeDollarSign, roles: ROLE_ADMIN_ONLY },
        { to: "/super-admin?tab=trials", label: "Trial Users", icon: Sparkles, roles: ROLE_ADMIN_ONLY },
        { to: "/admin/contact-messages", label: "Leads / Demo Requests", icon: Mail, roles: ROLE_ADMIN_ONLY },
        { to: "/admin/support-access", label: "Support Access Logs", icon: ShieldAlert, roles: ROLE_ADMIN_ONLY },
        { to: "/audit-trail?scope=platform", label: "Security Logs", icon: History, roles: ROLE_ADMIN_ONLY },
        { to: "/saas-ops", label: "System Settings", icon: DatabaseZap, roles: ROLE_ADMIN_ONLY },
        { to: "/legal-config", label: "Legal / Compliance", icon: Award, roles: ROLE_ADMIN_ONLY },
        { to: "/compliance-cockpit", label: "Compliance Cockpit", icon: Activity, roles: ROLE_ADMIN_ONLY },
    ],
};

/** Mobile bottom tabs (5 slots). "add" and "more" trigger sheets/drawer. */
export const BOTTOM_TABS = [
    { id: "home",    to: "/dashboard",      label: "Home",    icon: HomeIcon,        exact: true },
    { id: "action",  to: "/finance-review", label: "Action",  icon: ClipboardCheck },
    { id: "add",     kind: "sheet",         label: "Add",     icon: Plus },
    { id: "reports", to: "/reports",        label: "Reports", icon: FileBarChart },
    { id: "more",    kind: "drawer",        label: "More",    icon: MenuIcon },
];

/** Sheet items surfaced when the bottom "Add" tab is tapped. */
export const BOTTOM_ADD_ACTIONS = [
    { to: "/quick-entry",             label: "Add Expense",     icon: Zap,             roles: ROLE_ADMIN_FIN_ENTRY },
    { to: "/smart-inbox?capture=1",   label: "Upload Receipt",  icon: Camera,          roles: ROLE_ADMIN_FIN_ENTRY },
    { to: "/payments?add=1",          label: "Record Payment",  icon: Banknote,        roles: ROLE_ADMIN_FIN },
    { to: "/email-capture",           label: "Upload Statement",icon: Upload,          roles: ROLE_ADMIN_FIN },
    { to: "/cash-desk?add=sale",      label: "Add Cash Entry",  icon: Coins,           roles: ROLE_ALL },
    { to: "/documents?add=1",         label: "Upload Document", icon: FolderLock,      roles: ROLE_ADMIN_FIN },
];
